<template>
  <div ref="scrollContainerRef" class="in-dm-chat-area" :class="{ 'in-dm-chat-area--empty': isEmpty }">
    <span v-if="isEmpty" class="in-dm-chat-area__empty" />
    <template v-else>
      <InDmChatRow
        v-for="(row, index) in chatRows"
        :key="index"
        :item="row.item"
        :show-avatar="row.showAvatar"
        :picture-url="account.pictureUrl"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import InDmChatRow from '@/client/components/_molecules/in_dm_chat_row'

import type { InDmChatItem } from './types'
import { useSequentialPlayback } from './useSequentialPlayback'

interface Props {
  account: {
    pictureUrl: string | null
  }
  chatItems: InDmChatItem[]
  playbackControl: {
    stepIntervalMs: number
    isPlaying: boolean
    isReset?: boolean
  }
}
const props = defineProps<Props>()

interface Emits {
  (e: 'update:playbackControl', value: Props['playbackControl']): void
  (e: 'update:visibleCount', value: number): void
}
const emit = defineEmits<Emits>()

const isReceiveItem = (item: InDmChatItem): boolean =>
  (item.type === 'message' && item.side === 'left') || item.type === 'media' || item.type === 'card'

const total = computed(() => props.chatItems.length)

const clearResetFlag = () => {
  if (props.playbackControl.isReset) {
    emit('update:playbackControl', { ...props.playbackControl, isReset: false })
  }
}

const scrollContainerRef = ref<HTMLElement | null>(null)

const { visibleCount } = useSequentialPlayback({
  total,
  stepIntervalMs: computed(() => props.playbackControl.stepIntervalMs),
  isPlaying: computed(() => props.playbackControl.isPlaying),
  isReset: computed(() => props.playbackControl.isReset ?? false),
  onComplete: clearResetFlag,
  scrollContainer: scrollContainerRef
})

watch(
  () => props.playbackControl.isPlaying,
  playing => {
    if (!playing) clearResetFlag()
  }
)

watch(visibleCount, count => emit('update:visibleCount', count))

const visibleItems = computed(() => props.chatItems.slice(0, visibleCount.value))
const isEmpty = computed(() => visibleItems.value.length === 0)

interface ChatRow {
  item: InDmChatItem
  showAvatar: boolean
}

const chatRows = computed<ChatRow[]>(() => {
  const items = visibleItems.value
  return items.map((item, index) => {
    if (!isReceiveItem(item)) return { item, showAvatar: false }
    // 次が受信でない（送信/日付/末尾）なら、この受信アイテムがブロック末尾＝アバターを表示する
    const next = items[index + 1]
    const isBlockTail = next === undefined || !isReceiveItem(next)
    return { item, showAvatar: isBlockTail }
  })
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/in-dm.scss' as *;
@use '@/client/assets/scss/variables.scss' as *;

.in-dm-chat-area {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: $spacing-unit;
  min-height: 0;
  padding: $spacing-unit * 2 12px;
  overflow-y: auto;
  background: $in-dm-chat-bg;
}

.in-dm-chat-area > :deep(*) {
  flex-shrink: 0;
}

.in-dm-chat-area--empty {
  align-items: center;
  justify-content: center;
  font-size: $font-small-size;
  color: $in-dm-empty-text;
}
</style>
